/**
 * useProjectChat — React Query data layer for the UNIFIED project-level chat (P0 redesign Phase D).
 *
 * ✅ **THIS HOOK IS THE STABLE CONTRACT.** Phase D item D9 shipped `UnifiedChatPanel` as an
 * explicitly throwaway harness around it, warning that the UI — not the hook — was what Phase F
 * would replace. That has now happened: **F1 deleted `UnifiedChatPanel` and rebuilt the UI as
 * `components/projects/phase0/chat/ProjectChatView`**, reusing every endpoint, type and cache key
 * below without modification. Current consumers are `ProjectChatView` (full-page and docked
 * variants) and `ResultRow`.
 *
 * Endpoints (see specs/api/projects.yaml):
 *   POST  /api/projects/{projectId}/chat    → 202, records the turn and enqueues the fan-out
 *   GET   /api/projects/{projectId}/chat    → the transcript with per-file results
 *   PATCH /api/projects/{projectId}/chat    → accept / reject ONE per-file proposal (D7)
 *
 * All three share one resource; the result id travels in the PATCH body because a
 * `/chat/results/{resultId}` sub-resource would cost 5 CloudFormation resources against 4 of
 * headroom (CR §7.5a). That is why this looks less REST-shaped than it otherwise would.
 */

import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import { toast } from "sonner";
import { ArtifactType, useRegenerateBrandedDoc } from "./useArtifacts";

/** Non-blocking warning shown when a chat-accepted edit's branded re-render fails. */
export const BRANDED_REFRESH_FAILED_MESSAGE =
  "Branded document could not be refreshed — use Regenerate in the Branded tab";

/** Per-file outcome. Mirrors `ProjectChatResolution` in packages/projects/types.ts (V040's CHECK). */
export type ProjectChatResolution =
  | "proposed"
  | "accepted"
  | "rejected"
  | "stale"
  | "skipped_locked"
  | "skipped_forbidden"
  | "failed";

export interface ProjectChatResult {
  result_id: string;
  artifact_type: ArtifactType;
  resolution: ProjectChatResolution;
  /** Skip reason or per-file failure detail — already user-facing copy from the server. */
  reason: string | null;
  diff: string | null;
  agent_note: string | null;
  /** Present only while `resolution === 'proposed'`. */
  proposed_content?: string | null;
  /**
   * true when accepting this proposal starts a NEW VERSION of a committed, signed-off artifact
   * (Item 4, §5.4c). Server-derived at read time from `artifact_edits.status`, so it describes
   * what Accept will do NOW rather than what was true when the proposal was drafted.
   *
   * OPTIONAL on purpose. It is absent on a resolved row (no Accept button to describe) and absent
   * on any payload from a backend that predates Item 4 — `undefined` is falsy, so `ResultRow`
   * degrades to the pre-Item-4 rendering instead of asserting a disclosure it cannot support.
   */
  starts_new_version?: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
}

export type ProjectChatMessageStatus =
  | "pending"
  | "processing"
  | "complete"
  | "failed";

export interface ProjectChatMessage {
  message_id: string;
  seq: number;
  role: "user" | "agent" | "system";
  body: string;
  /** On a user turn: files TAGGED. On an agent turn: files actually TOUCHED. */
  tagged_artifact_types: ArtifactType[];
  status: ProjectChatMessageStatus | null;
  error_message: string | null;
  actor: string | null;
  /** True for turns reconstructed by the C2 backfill — authorship is unrecoverable. */
  imported: boolean;
  created_at: string;
  results: ProjectChatResult[];
}

export interface ProjectChatResponse {
  session_id: string | null;
  project_id: string;
  created_at: string | null;
  updated_at: string | null;
  messages: ProjectChatMessage[];
  next_before_seq: number | null;
}

export interface PostProjectChatResponse {
  session_id: string;
  message_id: string;
  seq: number;
  agent_message_id: string | null;
  status: ProjectChatMessageStatus | null;
  results: ProjectChatResult[];
}

export interface PatchProjectChatResultResponse {
  result_id: string;
  artifact_type: ArtifactType;
  resolution: ProjectChatResolution;
  applied: boolean;
  content: string | null;
}

export function projectChatQueryKey(projectId: string): [string, string] {
  return ["project-chat", projectId];
}

/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * LIVE STREAM STATE (CR items **E1** + **E7**)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Deliberately a SEPARATE query key from {@link projectChatQueryKey}.
 *
 * E7's non-negotiable is that the 5-second poll and the WebSocket push must not write the same key.
 * Removing the poll is half of that; the other half is not reintroducing the collision by having the
 * socket mutate the transcript cache. So:
 *
 *   • `['project-chat', projectId]`         ← server truth. Written ONLY by a fetch.
 *   • `['project-chat-stream', projectId]`  ← ephemeral. Written ONLY by the socket.
 *
 * The transcript is refetched exactly ONCE per turn, on the `complete`/`failed` stage. Nothing
 * periodic writes either key, so there is no window in which a stale response can clobber live
 * state. Stream state is never persisted and is dropped when the turn ends.
 */
export function projectChatStreamQueryKey(projectId: string): [string, string] {
  return ["project-chat-stream", projectId];
}

/** Stage names the worker emits. Mirrors `WORKER_EMITTED_STAGES` in agent-stream.service.ts. */
export type AgentStreamStage =
  | "queued"
  | "routing"
  | "directive_resolution"
  | "drafting"
  | "validating"
  | "computing_diff"
  | "complete"
  | "failed"
  | "heartbeat"
  | "note_delta";

/** Live state for ONE in-flight turn. */
export interface ProjectChatStreamState {
  message_id: string;
  /** Turn-level stage. Per-file stages update `per_file` instead of overwriting this. */
  stage: AgentStreamStage;
  /** Per-file stage, because fan-out branches run concurrently and advance independently. */
  per_file: Partial<Record<ArtifactType, AgentStreamStage>>;
  /** Accumulated narration, per file. */
  notes: Partial<Record<ArtifactType, string>>;
  artifact_types: ArtifactType[];
  agent?: string;
  /** Highest `seq` applied. Out-of-order frames are dropped rather than re-rendered backwards. */
  seq: number;
  terminal: boolean;
}

/**
 * Human-readable stage labels.
 *
 * ⚠️ `routing` must NOT say "orchestrator". Phase D item D2 deleted the orchestrator's chat mode —
 * the worker resolves the owning sub-agent itself and calls its runtime directly. A label naming a
 * hop that no longer happens is exactly the dishonesty the streaming research warned about.
 */
export const AGENT_STREAM_STAGE_LABELS: Record<AgentStreamStage, string> = {
  queued: "Queued…",
  routing: "Assigning to agents…",
  directive_resolution: "Coordinating changes across files…",
  drafting: "Drafting changes…",
  validating: "Checking the response…",
  computing_diff: "Computing the diff…",
  complete: "Done",
  failed: "Failed",
  heartbeat: "Still working…",
  note_delta: "Drafting changes…",
};

/**
 * Subscribe to live stage/narration state for a project's in-flight turn.
 *
 * Read-only: the socket writes this key (see `useWebSocket.ts`), this hook only observes it. It is a
 * `useQuery` with no `queryFn` so it participates in the same cache without ever fetching.
 */
export function useProjectChatStream(
  projectId: string,
): ProjectChatStreamState | undefined {
  const queryClient = useQueryClient();
  const { data } = useQuery<ProjectChatStreamState | undefined>({
    queryKey: projectChatStreamQueryKey(projectId),
    // No queryFn: this cache entry is push-only. `enabled: false` keeps React Query from ever
    // attempting a fetch while still subscribing the component to cache updates.
    enabled: false,
    initialData: () =>
      queryClient.getQueryData<ProjectChatStreamState>(
        projectChatStreamQueryKey(projectId),
      ),
  });
  return data;
}

/**
 * Fetch the transcript.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * NO POLLING — CR item **E7**, and it HAD to ship with E1
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * This query used `refetchInterval: 5000` while any turn was `pending`/`processing`. That is gone.
 *
 * The reason it could not be left in alongside streaming is not tidiness: the poll and the socket
 * write the same cache entry, and a poll response that was in flight when a stream frame landed
 * would overwrite live partial state with a snapshot of the server from ~5 s ago. The visible symptom
 * is flicker — stages advancing and then jumping backwards. The CR calls this out as Phase E's
 * "non-negotiable".
 *
 * What replaces it:
 *   • live progress  → `['project-chat-stream', projectId]`, written by the WebSocket (E1).
 *   • turn finished  → ONE invalidation of this key, triggered by the `complete`/`failed` stage.
 *   • socket dropped → ONE recovery fetch, triggered by the `ws-online` event on reconnect, and by
 *                      React Query's own `refetchOnWindowFocus`. Both are single fetches, not timers.
 *
 * `refetchOnWindowFocus` is set explicitly rather than left to the default so that "this query
 * recovers by event, never by interval" is a property stated in the code and not an inherited one a
 * future default change could silently alter.
 *
 * ⛔ Do NOT reintroduce `refetchInterval` here. If streaming ever needs a safety net, add a bounded
 * one-shot timeout that fires a SINGLE refetch and then stops — not a repeating interval.
 */
export function useProjectChat(projectId: string, enabled = true) {
  const client = useApiClient();

  return useQuery({
    queryKey: projectChatQueryKey(projectId),
    queryFn: async () => {
      const response = await client.get<ProjectChatResponse>(
        `/api/projects/${projectId}/chat`,
      );
      return response.data;
    },
    enabled: enabled && !!projectId,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

/**
 * Single reconnect-recovery refetch (CR item **E7**).
 *
 * Mount once per chat surface. `useWebSocket` dispatches `ws-online` on every successful (re)connect;
 * any frames missed while the socket was down are recovered by one fetch of the durable transcript,
 * which is the source of truth. Stream state is intentionally NOT reconstructed — partial narration
 * from a dropped connection is not worth replaying, and the result rows carry the outcome.
 */
export function useProjectChatReconnectRecovery(projectId: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!projectId) return;
    const onOnline = (): void => {
      void queryClient.invalidateQueries({
        queryKey: projectChatQueryKey(projectId),
      });
    };
    window.addEventListener("ws-online", onOnline);
    return () => window.removeEventListener("ws-online", onOnline);
  }, [projectId, queryClient]);
}

export interface PostProjectChatVariables {
  message: string;
  /** Up to five. Empty = a conversational message with no edit. */
  taggedArtifactTypes: ArtifactType[];
}

/** POST a turn. Returns 202 — the result arrives via the transcript poll, not this response. */
export function usePostProjectChatMessage(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (variables: PostProjectChatVariables) => {
      const response = await client.post<PostProjectChatResponse>(
        `/api/projects/${projectId}/chat`,
        {
          message: variables.message,
          tagged_artifact_types: variables.taggedArtifactTypes,
        },
      );
      return response.data;
    },
    onSuccess: () => {
      // Refetch immediately so the user's own turn and any pre-flight skips appear at once; the poll
      // then takes over for the agent turn.
      void queryClient.invalidateQueries({
        queryKey: projectChatQueryKey(projectId),
      });
    },
  });
}

export interface ResolveProjectChatResultVariables {
  resultId: string;
  action: "accept" | "reject";
  /** Echo of the hash the UI rendered. Supplementary to the server-side staleness check (D7). */
  baseContentHash?: string;
}

/**
 * Accept or reject ONE per-file proposal (CR item **D7**).
 *
 * 🚨 Use THIS, never `usePatchArtifact`, for a unified-chat result. The legacy PATCH writes
 * `artifact_edits.content` directly and knows nothing about result rows — so it would skip the
 * content-hash staleness check (silently overwriting a newer edit), never record
 * `resolution='accepted'`, and leave the proposal permanently open in the transcript.
 *
 * 🚨 Do NOT build an "accept all" on top of this. SEC-7 was closed by risk acceptance on the explicit
 * basis that a human reviews each proposal before it reaches a live document; batching that away voids
 * the acceptance (CR §8 OQ-4).
 */
export function useResolveProjectChatResult(projectId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();
  // Chat-accepted edits rewrite `artifact_edits.content`, so a branded artifact's rendered DOCX /
  // PPTX is stale the instant the PATCH succeeds. Nothing used to re-render it: this onSuccess
  // invalidated the markdown query and stopped, and the only regeneration trigger in the app lived
  // behind a ref that `BrandedDocPane` published — a component that is not mounted unless the user
  // is looking at the Branded tab. The result was a branded document that silently diverged from
  // its own markdown for every chat-driven edit, which is the primary editing surface.
  const { mutateAsync: regenerateBranded } = useRegenerateBrandedDoc(projectId);

  return useMutation({
    mutationFn: async (variables: ResolveProjectChatResultVariables) => {
      const response = await client.patch<PatchProjectChatResultResponse>(
        `/api/projects/${projectId}/chat`,
        {
          result_id: variables.resultId,
          action: variables.action,
          ...(variables.baseContentHash
            ? { base_content_hash: variables.baseContentHash }
            : {}),
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: projectChatQueryKey(projectId),
      });
      if (data.applied) {
        // The artifact changed — the editor's own cache is now stale.
        void queryClient.invalidateQueries({
          queryKey: ["artifact", projectId, data.artifact_type],
        });
        // …and so is its branded rendering, if it has one. A no-op for the three artifact types
        // that do not, so this stays unconditional (see useRegenerateBrandedDoc). Fire-and-forget:
        // the accept itself has already succeeded and must not be reported as failed because a
        // downstream re-render did not, and the Branded pane's Regenerate button is the recovery.
        //
        // Not SILENT, though: the server also re-renders after the accept (branded-regen.service),
        // but the user cannot see that, so a failure here is surfaced as a non-blocking warning that
        // names the recovery. The global axios toast is suppressed for this request so the user does
        // not get a generic "Server error" that reads as if the accept itself failed.
        void regenerateBranded({
          artifactType: data.artifact_type,
          suppressErrorToast: true,
        }).catch(() => {
          toast.warning(BRANDED_REFRESH_FAILED_MESSAGE);
        });
      }
    },
  });
}
